import _ from "lodash";
import { DateTime } from "luxon";
import { createSelector } from "reselect";
import { ext_crm } from "../context";

const private_website = import.meta.env.VITE_APP_PRIVATE_WEBSITE === "true";

const getGuestProfile = (state) => state.GuestProfile;

const getDescriptiveSections = (state) =>
  _.get(
    state.WebsiteMetadata,
    "metadata.landing_page.descriptive_sections",
    []
  );

function internalIsElligibleToSearch(guestProfile) {
  if (!guestProfile.with_subscription) return true;
  if (guestProfile.in_subscription) return true;
  if (guestProfile.in_free_period) return true;
  return false;
}

function externalBaseCheck(extProfile) {
  if (!extProfile) return false;
  if (!extProfile.stripe_customer_id) return false;
  return true;
}

function externalSubscriptionCheck(extProfile) {
  if (extProfile.free_membership) return true;

  const now = DateTime.now();
  const subStart = DateTime.fromISO(
    _.get(extProfile, "subscription.start_date"),
    { setZone: true }
  );
  const subEnd = DateTime.fromISO(_.get(extProfile, "subscription.end_date"), {
    setZone: true,
  });
  if (subStart <= now && now <= subEnd) return true;

  return false;
}

function externalIsElligibleToSearch(guestProfile) {
  const extProfile = _.get(guestProfile, "extProfile");

  if (!externalBaseCheck(extProfile)) return false;
  const now = DateTime.now();
  const inTrial = now <= DateTime.fromISO(extProfile.trial_end_date);

  if (inTrial) return true;
  if (externalSubscriptionCheck(extProfile)) return true;

  return false;
}

export const isElligibleToSearchSelector = createSelector(
  [getGuestProfile],
  (guestProfile) => {
    // If we are using our own crm authentication
    if (!ext_crm) {
      return internalIsElligibleToSearch(guestProfile);
    } else {
      return externalIsElligibleToSearch(guestProfile);
    }
  }
);

function internalElligibleToBook(guestProfile) {
  if (!guestProfile.with_subscription) return true;
  if (guestProfile.in_subscription) return true;

  return false;
}
function externalElligibleToBook(guestProfile) {
  const extProfile = _.get(guestProfile, "extProfile");

  if (!externalBaseCheck(extProfile)) return false;
  if (externalSubscriptionCheck(extProfile)) return true;

  return false;
}

export const isElligibleToBookSelector = createSelector(
  [getGuestProfile],
  (guestProfile) => {
    if (!private_website) return true;

    if (!ext_crm) {
      return internalElligibleToBook(guestProfile);
    } else {
      return externalElligibleToBook(guestProfile);
    }
  }
);

export const getMainPromoGenSectionsSelector = createSelector(
  [getDescriptiveSections],
  (descriptiveSections) => {
    return _.orderBy(descriptiveSections, "order").filter(
      (sect) => sect.in_body
    );
  }
);

export const getNavbarGenSectionsSelector = createSelector(
  [getDescriptiveSections],
  (descriptiveSections) => {
    return _.orderBy(
      descriptiveSections.filter((sect) => sect.in_navbar),
      "order"
    );
  }
);

export const getFooterGenSectionsSelector = createSelector(
  [getDescriptiveSections],
  (descriptiveSections) => {
    return _.orderBy(
      descriptiveSections.filter((sect) => sect.in_footer),
      "order"
    );
  }
);

function getTermsAndConditions(state) {
  return state?.WebsiteMetadata?.metadata?.terms_and_conditions?.terms_en ?? [];
}
export const getTermsAndConditionsSelector = createSelector(
  [getTermsAndConditions],
  (termsAndConditions) => {
    return termsAndConditions;
  }
);

export function hasFAQ(state) {
  return (
    (state?.WebsiteMetadata?.metadata?.terms_and_conditions?.terms_en ?? [])
      .length > 0
  );
}

function getPriceDecimals(state) {
  return state.WebsiteMetadata?.metadata?.price_decimal_places ?? 2;
}
export const getPriceDecimalsSelector = createSelector(
  [getPriceDecimals],
  (priceDecimals) => priceDecimals
);
